import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { notify } from '@core/notifications/notify';
import { ConfirmModal } from '@components/modal';
import {
  dataValidationService,
  ValidationRule,
  ValidationResult,
  AnomalyDetection,
  DataQualityReport,
  ValidationConfig,
  ValidationType,
  ValidationSeverity,
  AnomalyType
} from '@domains/compliance/execution/services/dataValidationService';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  X,
  RefreshCw,
  Settings,
  Plus,
  Edit,
  Trash2,
  Eye,
  BarChart3,
  Zap,
  Target,
  Activity,
  TrendingUp,
  AlertCircle,
  CheckSquare,
  Clock
} from 'lucide-react';

const DataValidationManager: React.FC = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState<'rules' | 'results' | 'anomalies' | 'reports' | 'config'>('rules');
  const [validationRules, setValidationRules] = useState<ValidationRule[]>([]);
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [anomalies, setAnomalies] = useState<AnomalyDetection[]>([]);
  const [dataQualityReports, setDataQualityReports] = useState<DataQualityReport[]>([]);
  const [config, setConfig] = useState<ValidationConfig | null>(null);
  const [selectedRule, setSelectedRule] = useState<ValidationRule | null>(null);
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showEditRuleModal, setShowEditRuleModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedAnomaly, setSelectedAnomaly] = useState<AnomalyDetection | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Form states
  const [newRule, setNewRule] = useState<Partial<ValidationRule>>({
    name: '',
    description: '',
    type: 'range_check',
    field_types: ['number'],
    parameters: {},
    is_active: true,
    severity: 'medium'
  });

  const [resolutionNotes, setResolutionNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const rules = await dataValidationService.getValidationRules();
      const anomalies = await dataValidationService.getAnomalies();
      const currentConfig = await dataValidationService.getConfig();

      // Generate sample validation results and reports for demonstration
      // In a real implementation, these would be fetched from the API
      const sampleResults: ValidationResult[] = [];
      const sampleReports: DataQualityReport[] = [
        {
          record_id: 1,
          overall_score: 85,
          completeness_score: 90,
          accuracy_score: 85,
          consistency_score: 80,
          validity_score: 90,
          timeliness_score: 80,
          issues: [],
          recommendations: ['Improve data consistency across related fields'],
          generated_at: new Date().toISOString()
        }
      ];

      setValidationRules(rules);
      setAnomalies(anomalies);
      setConfig(currentConfig);
      setValidationResults(sampleResults);
      setDataQualityReports(sampleReports);
    } catch (error) {
      console.error('Failed to load validation data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleAddRule = useCallback(async () => {
    if (!newRule.name || !newRule.type) {
      notify.error('Please fill in rule name and type');
      return;
    }

    try {
      const rule: ValidationRule = {
        id: `rule_${Date.now()}`,
        name: newRule.name,
        description: newRule.description || '',
        type: newRule.type as ValidationType,
        field_types: newRule.field_types || [],
        parameters: newRule.parameters || {},
        is_active: newRule.is_active || true,
        severity: newRule.severity as ValidationSeverity,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const success = await dataValidationService.addValidationRule(rule);
      if (success) {
        setValidationRules(prev => [...prev, rule]);
        setShowAddRuleModal(false);
        setNewRule({
          name: '',
          description: '',
          type: 'range_check',
          field_types: ['number'],
          parameters: {},
          is_active: true,
          severity: 'medium'
        });
        notify.success('Validation rule added successfully');
      } else {
        console.error('Failed to add validation rule');
      }
    } catch (error) {
      console.error('Failed to add validation rule:', error);
      console.error('Failed to add validation rule');
    }
  }, [newRule]);

  const handleUpdateRule = useCallback(async (ruleId: string, updates: Partial<ValidationRule>) => {
    try {
      const success = await dataValidationService.updateValidationRule(ruleId, updates);
      if (success) {
        setValidationRules(prev =>
          prev.map(rule =>
            rule.id === ruleId ? { ...rule, ...updates, updated_at: new Date().toISOString() } : rule
          )
        );
        // Update selected rule if it's currently being edited
        if (selectedRule?.id === ruleId) {
          setSelectedRule(prev => prev ? { ...prev, ...updates } : null);
        }
        // Log the update for debugging and analytics
        console.log('Validation rule updated:', { ruleId, updates });
        const actionMessage = updates.is_active !== undefined
          ? `Validation rule ${updates.is_active ? 'activated' : 'deactivated'} successfully`
          : 'Validation rule updated successfully';
        notify.success(actionMessage);
      } else {
        notify.error('Failed to update validation rule');
      }
    } catch (error) {
      console.error('Failed to update validation rule:', error);
      alert('Failed to update validation rule');
    }
  }, [selectedRule]);

  const handleSaveEditedRule = useCallback(async () => {
    if (!selectedRule) return;

    try {
      const success = await dataValidationService.updateValidationRule(selectedRule.id, {
        name: selectedRule.name,
        description: selectedRule.description,
        type: selectedRule.type,
        field_types: selectedRule.field_types,
        parameters: selectedRule.parameters,
        is_active: selectedRule.is_active,
        severity: selectedRule.severity
      });

      if (success) {
        setValidationRules(prev =>
          prev.map(rule =>
            rule.id === selectedRule.id ? { ...selectedRule, updated_at: new Date().toISOString() } : rule
          )
        );
        setShowEditRuleModal(false);
        setSelectedRule(null);
        notify.success('Validation rule updated successfully');
      } else {
        notify.error('Failed to update validation rule');
      }
    } catch (error) {
      console.error('Failed to update validation rule:', error);
      alert('Failed to update validation rule');
    }
  }, [selectedRule]);

  // Use handleUpdateRule in the UI by adding an edit button
  const handleEditRule = (rule: ValidationRule) => {
    setSelectedRule(rule);
    setShowEditRuleModal(true);
  };

  const handleDeleteRule = useCallback(async (ruleId: string) => {
    setSelectedRule(validationRules.find(r => r.id === ruleId) || null);
    setShowDeleteConfirmModal(true);
  }, [validationRules]);

  const confirmDeleteRule = useCallback(async () => {
    if (!selectedRule) return;

    try {
      const success = await dataValidationService.deleteValidationRule(selectedRule.id);
      if (success) {
        setValidationRules(prev => prev.filter(rule => rule.id !== selectedRule.id));
        notify.success('Validation rule deleted successfully');
        setShowDeleteConfirmModal(false);
        setSelectedRule(null);
      } else {
        notify.error('Failed to delete validation rule');
      }
    } catch (error) {
      console.error('Failed to delete validation rule:', error);
      notify.error('Failed to delete validation rule');
    }
  }, [selectedRule]);

  const handleResolveAnomaly = useCallback(async () => {
    if (!selectedAnomaly || !resolutionNotes.trim()) {
      notify.error('Please provide resolution notes');
      return;
    }

    try {
      const success = await dataValidationService.resolveAnomaly(selectedAnomaly.id, resolutionNotes);
      if (success) {
        setAnomalies(prev =>
          prev.map(anomaly =>
            anomaly.id === selectedAnomaly.id
              ? { ...anomaly, resolved_at: new Date().toISOString(), resolution_notes: resolutionNotes }
              : anomaly
          )
        );
        setShowResolveModal(false);
        setSelectedAnomaly(null);
        setResolutionNotes('');
        notify.success('Anomaly resolved successfully');
      } else {
        notify.error('Failed to resolve anomaly');
      }
    } catch (error) {
      console.error('Failed to resolve anomaly:', error);
      alert('Failed to resolve anomaly');
    }
  }, [selectedAnomaly, resolutionNotes]);

  const handleUpdateConfig = useCallback(async (updates: Partial<ValidationConfig>) => {
    try {
      const success = await dataValidationService.updateConfig(updates);
      if (success) {
        setConfig(prev => prev ? { ...prev, ...updates } : null);
        notify.success('Validation configuration updated successfully');
      } else {
        notify.error('Failed to update configuration');
      }
    } catch (error) {
      console.error('Failed to update configuration:', error);
      alert('Failed to update configuration');
    }
  }, []);

  const getSeverityColor = (severity: ValidationSeverity) => {
    const colors: Record<ValidationSeverity, string> = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pass: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      fail: 'bg-red-100 text-red-800',
      error: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getAnomalyTypeIcon = (type: AnomalyType) => {
    const icons: Record<AnomalyType, React.ReactNode> = {
      statistical_outlier: <BarChart3 className="text-orange-600" size={20} />,
      trend_deviation: <TrendingUp className="text-blue-600" size={20} />,
      pattern_anomaly: <Activity className="text-purple-600" size={20} />,
      data_quality_issue: <AlertCircle className="text-red-600" size={20} />,
      compliance_violation: <Shield className="text-red-600" size={20} />,
      system_error: <Zap className="text-yellow-600" size={20} />,
      fraud_detection: <AlertTriangle className="text-red-600" size={20} />,
      seasonal_anomaly: <Clock className="text-green-600" size={20} />,
      sudden_change: <Zap className="text-orange-600" size={20} />,
      missing_data: <Eye className="text-slate-600" size={20} />
    };
    return icons[type] || <AlertCircle className="text-slate-600" size={20} />;
  };

  if (!hasPermission('templates.update')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-red-600 px-8 py-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <AlertTriangle className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Access Denied</h2>
                <p className="text-red-100 text-sm mt-0.5">Restricted Area</p>
              </div>
            </div>
          </div>
          <div className="p-8">
            <p className="text-slate-600">Only Super Admin and Compliance Admin can access Data Validation Management.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="bg-amber-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Shield className="text-white" size={32} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Data Validation & Anomaly Detection</h1>
                  <p className="text-amber-100 mt-1">Monitor data quality and detect anomalies in real-time</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowAddRuleModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-amber-600 font-medium rounded-xl hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-amber-600 transition-all shadow-lg"
                >
                  <Plus size={20} />
                  Add Rule
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="border-b border-slate-100">
            <nav className="flex -mb-px">
              {[
                { id: 'rules', label: 'Validation Rules', icon: CheckSquare, color: 'text-slate-600' },
                { id: 'results', label: 'Validation Results', icon: CheckCircle, color: 'text-green-600' },
                { id: 'anomalies', label: 'Anomalies', icon: AlertTriangle, color: 'text-red-600' },
                { id: 'reports', label: 'Quality Reports', icon: BarChart3, color: 'text-blue-600' },
                { id: 'config', label: 'Configuration', icon: Settings, color: 'text-purple-600' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 py-4 px-6 border-b-2 font-medium text-sm transition-all ${
                    activeTab === tab.id
                      ? 'border-amber-500 text-amber-600 bg-amber-50'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <tab.icon size={18} className={activeTab === tab.id ? 'text-amber-600' : tab.color} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Validation Rules Tab */}
          {activeTab === 'rules' && (
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <CheckSquare className="text-slate-600" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Validation Rules</h2>
                  <p className="text-sm text-slate-600">Configure data validation rules and policies</p>
                </div>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <RefreshCw className="text-amber-600 animate-spin mr-3" size={24} />
                  <p className="text-slate-600 font-medium">Loading validation rules...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Rule Name</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Field Types</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Severity</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {validationRules.map((rule) => (
                        <tr key={rule.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4 whitespace-nowrap text-left">
                            <div>
                              <div className="text-sm font-medium text-slate-900">{rule.name}</div>
                              <div className="text-sm text-slate-500">{rule.description}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900">
                            {rule.type.replace('_', ' ')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900">
                            {rule.field_types.join(', ')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(rule.severity)}`}>
                              {rule.severity}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              rule.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'
                            }`}>
                              {rule.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  handleEditRule(rule);
                                  setSelectedRule(rule);
                                }}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-slate-600 text-white text-xs font-medium rounded-lg hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
                              >
                                <Edit size={14} />
                                Edit
                              </button>
                              <button
                                onClick={() => handleUpdateRule(rule.id, { is_active: !rule.is_active })}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all"
                              >
                                {rule.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                onClick={() => handleDeleteRule(rule.id)}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all"
                              >
                                <Trash2 size={14} />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Validation Results Tab */}
          {activeTab === 'results' && (
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="text-green-600" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Validation Results</h2>
                  <p className="text-sm text-slate-600">Review validation outcomes and error reports</p>
                </div>
              </div>
              <div className="space-y-4">
                {validationResults.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <CheckCircle className="text-green-500 mb-4" size={64} />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No validation results</h3>
                    <p className="text-slate-600 max-w-md">Validation results will appear here once data validation processes run.</p>
                  </div>
                ) : (
                  validationResults.map((result) => (
                    <div key={`${result.rule_id}_${result.record_id}`} className="p-4 rounded-xl border border-slate-200 hover:shadow-md transition-all">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-slate-900">{result.field_name}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(result.severity)}`}>
                            {result.severity}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(result.status)}`}>
                            {result.status}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-700 mb-3">{result.message}</p>
                      {result.suggested_action && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-sm text-blue-800">
                            <strong>Suggested Action:</strong> {result.suggested_action}
                          </p>
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mt-3">
                        Rule: {result.rule_name} | Record ID: {result.record_id} | {result.timestamp}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Anomalies Tab */}
          {activeTab === 'anomalies' && (
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="text-red-600" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Detected Anomalies</h2>
                  <p className="text-sm text-slate-600">Review and resolve data anomalies</p>
                </div>
              </div>
              <div className="space-y-4">
                {anomalies.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <CheckCircle className="text-green-500 mb-4" size={64} />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No anomalies detected</h3>
                    <p className="text-slate-600 max-w-md">All data appears to be within normal parameters.</p>
                  </div>
                ) : (
                  anomalies.map((anomaly) => (
                    <div key={anomaly.id} className="p-4 rounded-xl border border-slate-200 hover:shadow-md transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-red-100 rounded-lg">
                            {getAnomalyTypeIcon(anomaly.type)}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900">{anomaly.description}</h3>
                            <div className="flex items-center gap-4 mt-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(anomaly.severity)}`}>
                                {anomaly.severity}
                              </span>
                              <span className="text-xs text-slate-500">
                                Confidence: {(anomaly.confidence * 100).toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        </div>
                        {!anomaly.resolved_at && (
                          <button
                            onClick={() => {
                              setSelectedAnomaly(anomaly);
                              setShowResolveModal(true);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white font-medium rounded-xl hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all shadow-sm"
                          >
                            <CheckCircle size={16} />
                            Resolve
                          </button>
                        )}
                      </div>

                      {anomaly.metrics && (
                        <div className="bg-slate-50 rounded-lg p-4 mb-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {anomaly.metrics.expected_value !== undefined && (
                              <div>
                                <span className="text-slate-600">Expected:</span>
                                <span className="font-medium text-slate-900 ml-1">{anomaly.metrics.expected_value.toFixed(2)}</span>
                              </div>
                            )}
                            {anomaly.metrics.actual_value !== undefined && (
                              <div>
                                <span className="text-slate-600">Actual:</span>
                                <span className="font-medium text-slate-900 ml-1">{anomaly.metrics.actual_value.toFixed(2)}</span>
                              </div>
                            )}
                            {anomaly.metrics.deviation_percentage !== undefined && (
                              <div>
                                <span className="text-slate-600">Deviation:</span>
                                <span className="font-medium text-slate-900 ml-1">{anomaly.metrics.deviation_percentage.toFixed(1)}%</span>
                              </div>
                            )}
                            {anomaly.metrics.z_score !== undefined && (
                              <div>
                                <span className="text-slate-600">Z-Score:</span>
                                <span className="font-medium text-slate-900 ml-1">{anomaly.metrics.z_score.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500">
                          Affected Records: {anomaly.affected_records.length} | Detected: {anomaly.detected_at}
                        </p>
                      </div>

                      {anomaly.resolved_at && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-800">
                            <strong>Resolved:</strong> {anomaly.resolved_at}
                          </p>
                          {anomaly.resolution_notes && (
                            <p className="text-sm text-green-700 mt-1">
                              <strong>Notes:</strong> {anomaly.resolution_notes}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Quality Reports Tab */}
          {activeTab === 'reports' && (
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <BarChart3 className="text-blue-600" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Data Quality Reports</h2>
                  <p className="text-sm text-slate-600">Comprehensive data quality assessments</p>
                </div>
              </div>
              <div className="space-y-4">
                {dataQualityReports.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <BarChart3 className="text-slate-400 mb-4" size={64} />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No quality reports</h3>
                    <p className="text-slate-600 max-w-md">Quality reports will be generated after data validation processes complete.</p>
                  </div>
                ) : (
                  dataQualityReports.map((report) => (
                    <div key={report.record_id} className="p-6 rounded-xl border border-slate-200 hover:shadow-md transition-all">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="font-semibold text-slate-900">Quality Report - Record {report.record_id}</h3>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-blue-600">{report.overall_score.toFixed(1)}</div>
                          <div className="text-sm text-slate-500">Overall Score</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">{report.completeness_score.toFixed(0)}</div>
                          <div className="text-sm text-slate-600">Completeness</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">{report.accuracy_score.toFixed(0)}</div>
                          <div className="text-sm text-slate-600">Accuracy</div>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-lg">
                          <div className="text-2xl font-bold text-yellow-600">{report.consistency_score.toFixed(0)}</div>
                          <div className="text-sm text-slate-600">Consistency</div>
                        </div>
                        <div className="text-center p-3 bg-purple-50 rounded-lg">
                          <div className="text-2xl font-bold text-purple-600">{report.validity_score.toFixed(0)}</div>
                          <div className="text-sm text-slate-600">Validity</div>
                        </div>
                        <div className="text-center p-3 bg-red-50 rounded-lg">
                          <div className="text-2xl font-bold text-red-600">{report.timeliness_score.toFixed(0)}</div>
                          <div className="text-sm text-slate-600">Timeliness</div>
                        </div>
                      </div>

                      {report.recommendations.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Target className="text-amber-600" size={20} />
                            <h4 className="font-semibold text-amber-900">Recommendations</h4>
                          </div>
                          <ul className="list-disc list-inside text-sm text-amber-800 space-y-1">
                            {report.recommendations.map((rec, index) => (
                              <li key={index}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <p className="text-xs text-slate-500 mt-4">
                        Generated: {report.generated_at}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Configuration Tab */}
          {activeTab === 'config' && config && (
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Settings className="text-purple-600" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Validation Configuration</h2>
                  <p className="text-sm text-slate-600">Configure validation settings and thresholds</p>
                </div>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Real-time Validation</label>
                        <p className="text-xs text-slate-500">Validate data as it's entered</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={config.enable_real_time_validation}
                        onChange={(e) => handleUpdateConfig({ enable_real_time_validation: e.target.checked })}
                        className="h-5 w-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500 focus:ring-offset-0"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Batch Validation</label>
                        <p className="text-xs text-slate-500">Validate data in batches</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={config.enable_batch_validation}
                        onChange={(e) => handleUpdateConfig({ enable_batch_validation: e.target.checked })}
                        className="h-5 w-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500 focus:ring-offset-0"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Anomaly Detection</label>
                        <p className="text-xs text-slate-500">Automatically detect anomalies</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={config.enable_anomaly_detection}
                        onChange={(e) => handleUpdateConfig({ enable_anomaly_detection: e.target.checked })}
                        className="h-5 w-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500 focus:ring-offset-0"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Auto-resolve Low Severity</label>
                        <p className="text-xs text-slate-500">Automatically resolve low-severity issues</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={config.auto_resolve_low_severity}
                        onChange={(e) => handleUpdateConfig({ auto_resolve_low_severity: e.target.checked })}
                        className="h-5 w-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500 focus:ring-offset-0"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Anomaly Sensitivity: {(config.anomaly_sensitivity * 100).toFixed(0)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={config.anomaly_sensitivity}
                        onChange={(e) => handleUpdateConfig({ anomaly_sensitivity: parseFloat(e.target.value) })}
                        className="w-full"
                      />
                      <p className="text-xs text-slate-500 mt-1">Higher values detect more anomalies</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Notification Threshold</label>
                      <select
                        value={config.notification_threshold}
                        onChange={(e) => handleUpdateConfig({ notification_threshold: e.target.value as ValidationSeverity })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Retention Period (days)</label>
                      <input
                        type="number"
                        value={config.retention_period_days}
                        onChange={(e) => handleUpdateConfig({ retention_period_days: parseInt(e.target.value) })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add Rule Modal */}
        {showAddRuleModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
            <div className="relative min-h-full flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
                {/* Modal Header */}
                <div className="bg-amber-600 px-8 py-6 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                        <Plus className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Add Validation Rule</h3>
                        <p className="text-amber-100 text-sm mt-0.5">Create a new data validation rule</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowAddRuleModal(false)}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-colors"
                    >
                      <X className="text-white" size={24} />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-8 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Rule Name *</label>
                      <input
                        type="text"
                        value={newRule.name}
                        onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                        placeholder="e.g., Range Check for Energy Values"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Validation Type</label>
                      <select
                        value={newRule.type}
                        onChange={(e) => setNewRule(prev => ({ ...prev, type: e.target.value as ValidationType }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                      >
                        <option value="range_check">Range Check</option>
                        <option value="required_check">Required Check</option>
                        <option value="format_check">Format Check</option>
                        <option value="consistency_check">Consistency Check</option>
                        <option value="business_rule">Business Rule</option>
                        <option value="statistical_anomaly">Statistical Anomaly</option>
                        <option value="completeness_check">Completeness Check</option>
                        <option value="duplicate_check">Duplicate Check</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Description</label>
                    <textarea
                      value={newRule.description}
                      onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      placeholder="Describe what this validation rule checks..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Severity</label>
                      <select
                        value={newRule.severity}
                        onChange={(e) => setNewRule(prev => ({ ...prev, severity: e.target.value as ValidationSeverity }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        id="rule-active"
                        checked={newRule.is_active}
                        onChange={(e) => setNewRule(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="h-5 w-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500 focus:ring-offset-0"
                      />
                      <label htmlFor="rule-active" className="ml-3 text-sm font-medium text-slate-700">Active Rule</label>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-8 py-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
                  <button
                    onClick={() => setShowAddRuleModal(false)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddRule}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white font-medium rounded-xl hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all shadow-sm"
                  >
                    <CheckCircle size={18} />
                    Add Rule
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resolve Anomaly Modal */}
        {showResolveModal && selectedAnomaly && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
            <div className="relative min-h-full flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
                {/* Modal Header */}
                <div className="bg-red-600 px-8 py-6 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                        <CheckCircle className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Resolve Anomaly</h3>
                        <p className="text-red-100 text-sm mt-0.5">Document the resolution of this data anomaly</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowResolveModal(false);
                        setSelectedAnomaly(null);
                        setResolutionNotes('');
                      }}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-colors"
                    >
                      <X className="text-white" size={24} />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-8 space-y-6">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Anomaly Details</label>
                    <p className="text-sm text-slate-900 font-medium">{selectedAnomaly.description}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(selectedAnomaly.severity)}`}>
                        {selectedAnomaly.severity}
                      </span>
                      <span className="text-xs text-slate-500">
                        Confidence: {(selectedAnomaly.confidence * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Resolution Notes *</label>
                    <textarea
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      rows={4}
                      placeholder="Describe how this anomaly was resolved..."
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-8 py-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowResolveModal(false);
                      setSelectedAnomaly(null);
                      setResolutionNotes('');
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResolveAnomaly}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white font-medium rounded-xl hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all shadow-sm"
                  >
                    <CheckCircle size={18} />
                    Resolve Anomaly
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Rule Modal */}
        {showEditRuleModal && selectedRule && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
            <div className="relative min-h-full flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
                {/* Modal Header */}
                <div className="bg-slate-700 px-8 py-6 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                        <Edit className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Edit Validation Rule</h3>
                        <p className="text-slate-100 text-sm mt-0.5">Modify the selected validation rule</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowEditRuleModal(false);
                        setSelectedRule(null);
                      }}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-colors"
                    >
                      <X className="text-white" size={24} />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-8 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Rule Name *</label>
                      <input
                        type="text"
                        value={selectedRule.name}
                        onChange={(e) => setSelectedRule(prev => prev ? { ...prev, name: e.target.value } : null)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                        placeholder="e.g., Range Check for Energy Values"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Validation Type</label>
                      <select
                        value={selectedRule.type}
                        onChange={(e) => setSelectedRule(prev => prev ? { ...prev, type: e.target.value as ValidationType } : null)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                      >
                        <option value="range_check">Range Check</option>
                        <option value="required_check">Required Check</option>
                        <option value="format_check">Format Check</option>
                        <option value="consistency_check">Consistency Check</option>
                        <option value="business_rule">Business Rule</option>
                        <option value="statistical_anomaly">Statistical Anomaly</option>
                        <option value="completeness_check">Completeness Check</option>
                        <option value="duplicate_check">Duplicate Check</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Description</label>
                    <textarea
                      value={selectedRule.description}
                      onChange={(e) => setSelectedRule(prev => prev ? { ...prev, description: e.target.value } : null)}
                      rows={3}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      placeholder="Describe what this validation rule checks..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Severity</label>
                      <select
                        value={selectedRule.severity}
                        onChange={(e) => setSelectedRule(prev => prev ? { ...prev, severity: e.target.value as ValidationSeverity } : null)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        id="edit-rule-active"
                        checked={selectedRule.is_active}
                        onChange={(e) => setSelectedRule(prev => prev ? { ...prev, is_active: e.target.checked } : null)}
                        className="h-5 w-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500 focus:ring-offset-0"
                      />
                      <label htmlFor="edit-rule-active" className="ml-3 text-sm font-medium text-slate-700">Active Rule</label>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-8 py-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowEditRuleModal(false);
                      setSelectedRule(null);
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEditedRule}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white font-medium rounded-xl hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all shadow-sm"
                  >
                    <CheckCircle size={18} />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmModal && (
          <ConfirmModal
            onClose={() => {
              setShowDeleteConfirmModal(false);
              setSelectedRule(null);
            }}
            onConfirm={confirmDeleteRule}
            title="Delete Validation Rule"
            message={`Are you sure you want to delete the validation rule "${selectedRule?.name}"? This action cannot be undone.`}
            confirmText="Delete Rule"
            cancelText="Cancel"
            type="danger"
          />
        )}
      </div>
    </div>
  );
};

export default DataValidationManager;